import { AuthOptions } from 'angular-auth-oidc-client';

/**
 * Per-call OIDC authorize parameters for the MFA / re-challenge policy (SYRC-7399).
 *
 * WHY PER CALL, NOT `customParamsAuthRequest` IN auth-config.module.ts. Static config params are
 * merged into EVERY authorize request, including AuthGuard's silent `prompt=none` probe. A static
 * `prompt=login` would collide with that `prompt=none`, so the parameters are passed on the calls
 * that need them instead.
 *
 * WHY `prompt=login` AND NOT `acr_values` / `max_age`. Whether a user needs MFA is decided per user
 * inside Auth0 (the flag is CorView's per-user toggle, read by the tenant at sign-in), not by the
 * request. `acr_values=multi-factor` would ask Auth0 for MFA for EVERYONE, including users whose
 * toggle is off. `prompt=login` instead forces a fresh authentication, which makes Auth0 run its
 * normal per-user MFA decision again: MFA users are challenged, non-MFA users just re-enter their
 * credentials. `max_age` is only needed for a freshness window the policy does not define.
 * Verified on the QA tenant: same request, MFA user challenged, MFA-off user not.
 */

/**
 * Parameters for an explicit sign-in from the login page.
 *
 * In Auth0 mode the login is always a fresh authentication (`prompt=login`), matching the CorView
 * app's login. Without it, an Auth0 session created by another app on the same tenant (CorView)
 * would sign the user in silently and skip the per-user MFA decision. Under the legacy STS the
 * Auth0-only params are meaningless, so only `login_hint` is sent.
 */
export function buildLoginAuthOptions(
  loginHint: string,
  auth0Enabled: boolean,
  connection: string | null,
): AuthOptions {
  const customParams: Record<string, string> = { login_hint: loginHint };

  if (auth0Enabled) {
    customParams['prompt'] = 'login';
    if (connection) {
      customParams['connection'] = connection;
    }
  }

  return { customParams };
}

/**
 * Parameters for re-authenticating after a token renewal failed for good.
 *
 * A failed renewal means the session can no longer be extended silently — the refresh token was
 * revoked, expired or rotated away, or the token endpoint kept failing. The library does not tell
 * us which (it wipes the tokens on any non-network refresh error and replaces the HTTP error with a
 * generic one), so they are all treated alike. Rather than let a silent bounce quietly re-establish
 * a session that may have been revoked at the IdP, force a fresh login so the user re-authenticates
 * (and Auth0 re-applies the per-user MFA decision).
 *
 * AUTH0 MODE ONLY. The callers must not use this under the legacy CorView STS, where the bounce
 * stays a plain authorize(): the per-user MFA decision is an Auth0 concern, and forcing a login
 * there would change behaviour for users who do not need it. AppComponent.reauthenticate() checks
 * the provider flag before calling this, like buildLoginAuthOptions() does for the login page.
 */
export function buildFreshLoginAuthOptions(): AuthOptions {
  return { customParams: { prompt: 'login' } };
}
